import { initializeApp } from 'firebase/app';
import {
  getAuth,
  connectAuthEmulator,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  type Auth,
  type User,
} from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';

/**
 * Firebase の設定
 * 本番環境では環境変数から取得、エミュレータ時はダミー値を使用
 */
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? 'demo-api-key',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? 'localhost',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? 'demo-carving',
};

// Firebase アプリの初期化（シングルトン）
const app = initializeApp(firebaseConfig);

// Firebase Auth の初期化
const auth: Auth = getAuth(app);

// Firestore の初期化（チャット機能の骨格）
const firestore = getFirestore(app);

// Identity Platform テナント ID（管理者用 Tenant A）
// エミュレータ使用時はダミー値を許容、本番環境では必須
const ADMIN_TENANT_ID = import.meta.env.VITE_ADMIN_TENANT_ID;
if (!ADMIN_TENANT_ID && import.meta.env.VITE_USE_EMULATOR !== 'true') {
  throw new Error('必須環境変数 VITE_ADMIN_TENANT_ID が設定されていません');
}
auth.tenantId = ADMIN_TENANT_ID ?? 'demo-tenant-id';

// エミュレータ接続（開発環境）
if (import.meta.env.VITE_USE_EMULATOR === 'true') {
  const emulatorHost = import.meta.env.VITE_AUTH_EMULATOR_HOST ?? 'localhost';
  const emulatorPort = import.meta.env.VITE_AUTH_EMULATOR_PORT ?? '9099';
  connectAuthEmulator(auth, `http://${emulatorHost}:${emulatorPort}`, {
    disableWarnings: true,
  });

  const firestoreHost = import.meta.env.VITE_FIRESTORE_EMULATOR_HOST ?? 'localhost';
  const firestorePort = import.meta.env.VITE_FIRESTORE_EMULATOR_PORT ?? '8080';
  connectFirestoreEmulator(firestore, firestoreHost, parseInt(firestorePort, 10));

  console.log('Firebase Emulator に接続しました');
}

/**
 * メールアドレスとパスワードでログイン
 * Identity Platform の Tenant A を使用
 */
async function signIn(email: string, password: string): Promise<User> {
  const credential = await signInWithEmailAndPassword(auth, email, password);
  return credential.user;
}

/**
 * ログアウト
 */
async function signOut(): Promise<void> {
  await firebaseSignOut(auth);
}

/**
 * 現在の認証済みユーザーの ID トークンを取得
 */
async function getIdToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken();
}

export { auth, firestore, signIn, signOut, getIdToken, onAuthStateChanged, type User };
